import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { useContext, useMemo } from 'react';
import { useEffectOnce } from 'packages/react-hooks/src';
import { InventoryTypeKey } from '~/constants/inventoryType';
import DiscountContext from '~/context/common/DiscountContext';
import GlobalContext from '~/context/vdp/GlobalContext';
import { useTransitChargeInfo } from '~/context/vdp/UserVehicleContext';
import { useGetVehicleDetails } from '~/context/vdp/VehicleContext';
import useExperimentBucket from '~/hooks/common/useExperimentBucket';
import { useGetShowEVTaxTreatment, usePriceDrop } from '~/hooks/vdp';
import { useShowKBBPromo } from '../StickyBanner/hooks';
import { useShouldFetchBulkAndShipping } from '../VehicleDetailsContainer/hooks';

export default function useHeroViewedEvent() {
  const userBucket = useExperimentBucket('car-merch-vdp-hero-refresh2');
  const { vehicleRating } = useContext(GlobalContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const showKBBPromo = useShowKBBPromo();
  const showEVTaxTreatment = useGetShowEVTaxTreatment();
  const { showPriceDrop, restrictedState } = usePriceDrop();
  const { isLoaded: isTransitChargeLoaded } = useTransitChargeInfo();
  const shouldFetchBulkAndShipping = useShouldFetchBulkAndShipping();
  const {
    discounts: { isLoaded: isDiscountsLoaded }
  } = useContext(DiscountContext);
  const isTaxCreditTagLoaded =
    isDiscountsLoaded &&
    (shouldFetchBulkAndShipping ? isTransitChargeLoaded : true);
  const { inventoryType } = useGetVehicleDetails();
  const isWIP = inventoryType === InventoryTypeKey.WIP;

  const shouldFireForWIP =
    (isWIP && userBucket === 'treatment' && vehicleRating.isLoaded) ||
    (isWIP && userBucket === 'control'); // transit charge does not load for WIP vehicles

  const shouldFireEvent =
    (isTaxCreditTagLoaded &&
      ((userBucket === 'treatment' && vehicleRating.isLoaded) ||
        userBucket === 'control')) ||
    shouldFireForWIP;

  const priceBadgeList = useMemo(() => {
    const badges: string[] = [];
    if (showPriceDrop) {
      badges.push(restrictedState ? 'New Price' : 'Price Drop');
    }
    if (showKBBPromo) {
      badges.push('Great Deal');
    }
    if (showEVTaxTreatment) {
      badges.push('Clean Vehicle Tax Credit');
    }

    return badges;
  }, [restrictedState, showEVTaxTreatment, showKBBPromo, showPriceDrop]);

  useEffectOnce(
    () => {
      trackEvent('Viewed - Section', {
        heroRefresh: userBucket,
        priceBadges: priceBadgeList,
        rating: vehicleRating.data?.averageRating || null,
        reviewCount:
          vehicleRating.data?.totalRatingCounts.toLocaleString() || null,
        section: 'Hero'
      });
    },
    [priceBadgeList, trackEvent, userBucket, vehicleRating],
    shouldFireEvent
  );
}
